import { addDays, addYears, isAfter, max, startOfDay, startOfYear } from 'date-fns';
import { serviceModel, serviceCostModel, makeAssignedUserUnassigned, makeAssignedUserAssigned } from '~/models';
import { DiscountPeriodExpirationModel } from '~/models/DiscountPeriodExpirationModel';
import { DiscountExpirationType, LimitedTimeDiscountOfferModel } from '~/models/LimitedTimeDiscountOfferModel';
import { fromDateStampObject } from '~/models/time';
import { AdvancedSetting, IncreaseMethod } from './ServiceAdvancedSettings.types';
import { StartAutomaticBillingAtControlDropdownMenuItem } from './StartAutomaticBillingAtControl/StartAutomaticBillingAtControl';
export function getStartAutomaticBillingAtSelection(service) {
    if (serviceModel.isPaused(service)) {
        return StartAutomaticBillingAtControlDropdownMenuItem.Pause;
    }
    if (serviceModel.startBillingAt(service)) {
        return StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate;
    }
    if (serviceModel.isBillOnPeriodBeforeNextBilling(service)) {
        return StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate;
    }
    return null;
}
const isSettingActiveForServiceIntegration = (service, getServiceIntegration)=>{
    const isSettingActiveForService = (service)=>{
        const integration = getServiceIntegration(service);
        return !!integration && (integration.isEnabled() || integration.hasAssignedTeamMembers());
    };
    if (service.bundle) {
        return service.bundle.services.some(isSettingActiveForService);
    }
    return isSettingActiveForService(service);
};
export const isSettingActive = (param)=>{
    let { type, service } = param;
    switch(type){
        case AdvancedSetting.Pause:
            return serviceModel.isPaused(service);
        case AdvancedSetting.BillOnApproval:
            return !serviceModel.isSkipBillingOnAcceptance(service);
        case AdvancedSetting.Prorate:
            return serviceModel.isProrated(service);
        case AdvancedSetting.Optional:
            return service.participation.type === 'optional';
        case AdvancedSetting.LimitCharges:
            return serviceModel.maxChargesEnabled(service);
        case AdvancedSetting.AutoPriceIncrease:
            {
                var _serviceChanges_cost;
                if (serviceModel.priceIncrease(service).exists()) {
                    return true;
                }
                const serviceChanges = serviceModel.changes(service);
                if (serviceChanges === null || serviceChanges === void 0 ? void 0 : (_serviceChanges_cost = serviceChanges.cost) === null || _serviceChanges_cost === void 0 ? void 0 : _serviceChanges_cost.priceIncrease) {
                    return serviceChanges.cost.priceIncrease.exists();
                }
                return false;
            }
        case AdvancedSetting.QboClasses:
            return serviceModel.isIntegratedClassSet(service);
        case AdvancedSetting.FinancialCents:
            {
                return isSettingActiveForServiceIntegration(service, (service)=>service.integrations.financialCentsIntegration());
            }
        case AdvancedSetting.Karbon:
            {
                return isSettingActiveForServiceIntegration(service, (service)=>service.integrations.karbonIntegration());
            }
        case AdvancedSetting.LimitedTimeDiscountOffer:
            return !!serviceModel.limitedTimeDiscountOffer(service);
        case AdvancedSetting.LimitedTimeDiscountOfferForAgreement:
            {
                var _serviceModel_discount_expiration, _serviceModel_discount, _serviceChanges_cost1;
                const discount = service.cost.discount;
                if (discount && ((_serviceModel_discount = serviceModel.discount(service)) === null || _serviceModel_discount === void 0 ? void 0 : (_serviceModel_discount_expiration = _serviceModel_discount.expiration()) === null || _serviceModel_discount_expiration === void 0 ? void 0 : _serviceModel_discount_expiration.hasExpiration())) {
                    return true;
                }
                const serviceChanges = serviceModel.changes(service);
                if (serviceChanges === null || serviceChanges === void 0 ? void 0 : (_serviceChanges_cost1 = serviceChanges.cost) === null || _serviceChanges_cost1 === void 0 ? void 0 : _serviceChanges_cost1.discount) {
                    var _serviceModel_discount_expiration1, _serviceModel_discount1;
                    return !!((_serviceModel_discount1 = serviceModel.discount({
                        cost: serviceChanges.cost
                    })) === null || _serviceModel_discount1 === void 0 ? void 0 : (_serviceModel_discount_expiration1 = _serviceModel_discount1.expiration()) === null || _serviceModel_discount_expiration1 === void 0 ? void 0 : _serviceModel_discount_expiration1.hasExpiration());
                }
                return false;
            }
        case AdvancedSetting.PreapprovedAmount:
            {
                var _serviceModel_changes;
                const cost = ((_serviceModel_changes = serviceModel.changes(service)) === null || _serviceModel_changes === void 0 ? void 0 : _serviceModel_changes.cost) || service.cost;
                if (serviceCostModel.preapprovedControlledByAdvancedSettings(cost)) {
                    return true;
                }
                if (serviceModel.hasPreapprovedAmount(service)) {
                    return true;
                }
                const serviceChanges = serviceModel.changes(service);
                if (serviceChanges === null || serviceChanges === void 0 ? void 0 : serviceChanges.cost) {
                    return serviceModel.hasPreapprovedAmount({
                        cost: serviceChanges.cost
                    });
                }
                return false;
            }
        case AdvancedSetting.StartAutomaticBillingAt:
            return getStartAutomaticBillingAtSelection(service) !== null;
        default:
            return false;
    }
};
export function getAmendedMinDate(now, relationshipEffectiveDate) {
    const tomorrowDate = addDays(now, 1);
    return relationshipEffectiveDate ? max([
        tomorrowDate,
        relationshipEffectiveDate
    ]) : tomorrowDate;
}
export function getInitialStartBillingAtDate(param) {
    let { now = new Date(), relationshipEffectiveDate = null, isAmendedService = false } = param;
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    if (isAmendedService) {
        return relationshipEffectiveDate ? max([
            tomorrow,
            relationshipEffectiveDate
        ]) : tomorrow;
    }
    if (relationshipEffectiveDate) {
        return max([
            startOfDay(relationshipEffectiveDate),
            today
        ]);
    }
    return tomorrow;
}
export function getMinimumEffectiveDate(param) {
    let { now = new Date(), relationshipEffectiveDate = null, isAmendedService = false } = param;
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    if (isAmendedService) {
        return relationshipEffectiveDate ? max([
            tomorrow,
            relationshipEffectiveDate
        ]) : tomorrow;
    }
    return relationshipEffectiveDate ? addYears(today, -1) : tomorrow;
}
export function updateStartBillingAtByMenuItem(param) {
    let { item, value, service, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange } = param;
    switch(item){
        case StartAutomaticBillingAtControlDropdownMenuItem.Pause:
            onPausedChange === null || onPausedChange === void 0 ? void 0 : onPausedChange(service.id, Boolean(value));
            break;
        case StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate:
            onBillOnAcceptanceChange === null || onBillOnAcceptanceChange === void 0 ? void 0 : onBillOnAcceptanceChange(service.id, Boolean(value));
            break;
        case StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate:
            onStartBillingAtChange === null || onStartBillingAtChange === void 0 ? void 0 : onStartBillingAtChange(service.id, value instanceof Date ? value : null);
            break;
    }
}
// price Increase Utils
export function getInitialIncreaseMethod(priceIncrease, relationshipState) {
    if (priceIncrease.exists()) {
        return priceIncrease.increaseOn() ? IncreaseMethod.Date : IncreaseMethod.EffectiveDate;
    } else {
        return relationshipState.type === 'awaitingApproval' && !relationshipState.effectiveDate ? IncreaseMethod.EffectiveDate : IncreaseMethod.Date;
    }
}
export function getValidIncreaseOn(increaseOn, now) {
    const today = startOfDay(now);
    return isAfter(increaseOn, today) ? increaseOn : addYears(increaseOn, 1);
}
export function defaultIncreaseOn(service, now) {
    const priceIncrease = serviceModel.priceIncrease(service);
    return priceIncrease.increaseOn() || startOfYear(addYears(now, 1));
}
export function defaultPrecentIncrease(service) {
    const priceIncrease = serviceModel.priceIncrease(service);
    if (!priceIncrease.exists() || priceIncrease.percentIncrease().toNumber() < 0) {
        return '';
    }
    return priceIncrease.percentIncrease().toString();
}
export function defaultRoundPrices(service) {
    return serviceModel.priceIncrease(service).roundPrices();
}
export function defaultIncreaseMethod(priceIncrease, relationshipState) {
    return getInitialIncreaseMethod(priceIncrease, relationshipState);
}
// Discount Period Expiration Utils
export function getInitialDiscountPeriodExpiration(param) {
    let { now = new Date(), isApprovedRelationship = false } = param;
    if (isApprovedRelationship) {
        const baseDate = startOfDay(now);
        return DiscountPeriodExpirationModel.makeDate(addYears(baseDate, 1));
    } else {
        return DiscountPeriodExpirationModel.makePeriod('years', 1);
    }
}
export function getInitialDiscountPeriodExpirationState(param) {
    let { now = new Date(), isApprovedRelationship = false, existingExpiration } = param;
    var _defaultExpiration_date, _defaultExpiration_period, _defaultExpiration_amount;
    if (existingExpiration && existingExpiration.type() !== 'none') {
        const date = existingExpiration.date();
        const period = existingExpiration.period();
        const amount = existingExpiration.amount();
        if (existingExpiration.isExpirationDateType() && date) {
            return {
                selectedDate: date,
                timeframeType: 'date',
                periodType: 'years',
                amount: 1
            };
        } else if (existingExpiration.isExpirationPeriodType() && period && amount !== undefined) {
            return {
                selectedDate: addYears(now, 1),
                timeframeType: 'period',
                periodType: period,
                amount
            };
        }
    }
    const defaultExpiration = getInitialDiscountPeriodExpiration({
        now,
        isApprovedRelationship
    });
    const defaultDate = (_defaultExpiration_date = defaultExpiration.date()) !== null && _defaultExpiration_date !== void 0 ? _defaultExpiration_date : addYears(now, 1);
    const defaultPeriod = (_defaultExpiration_period = defaultExpiration.period()) !== null && _defaultExpiration_period !== void 0 ? _defaultExpiration_period : 'years';
    const defaultAmount = (_defaultExpiration_amount = defaultExpiration.amount()) !== null && _defaultExpiration_amount !== void 0 ? _defaultExpiration_amount : 1;
    return {
        selectedDate: defaultDate,
        timeframeType: isApprovedRelationship ? 'date' : 'period',
        periodType: defaultPeriod,
        amount: defaultAmount
    };
}
export function getInitialLimitedTimeDiscountOffer() {
    const expiration = {
        type: DiscountExpirationType.Days,
        days: 30
    };
    return new LimitedTimeDiscountOfferModel(expiration);
}
export function getInitialLimitedTimeDiscountOfferState(param) {
    let { now = new Date(), existingOffer } = param;
    if (existingOffer) {
        const expirationType = existingOffer.DiscountExpirationType();
        const days = existingOffer.ExpirationDays();
        const date = existingOffer.ExpirationDate();
        if (expirationType === DiscountExpirationType.Days && days !== undefined) {
            return {
                timeframeType: 'within',
                daysCount: days,
                selectedDate: addDays(now, days)
            };
        } else if (expirationType === DiscountExpirationType.Date && date) {
            return {
                timeframeType: 'until',
                daysCount: 30,
                selectedDate: fromDateStampObject(date)
            };
        }
    }
    const defaultDays = 30;
    return {
        timeframeType: 'within',
        daysCount: defaultDays,
        selectedDate: addDays(now, defaultDays)
    };
}
export function getUserForRole(newUserId) {
    return newUserId ? makeAssignedUserAssigned(newUserId) : makeAssignedUserUnassigned();
}
